import { AsyncPipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';

import { MediaAdapter } from '@modules/content-api/adapter/media.adapter';
import { ContentAPIService } from '@modules/content-api/content-api.service';
import { InputSanitizer } from '@modules/content-api/input.sanitizer';
import { FavoriteDataContextInterface } from '@modules/favorites/interfaces/favorite-data-context.interface';
import { MediaAudioInterface, MediaAudioModel } from '@modules/media/models/media-audio.model';
import { MediaDocumentInterface, MediaDocumentModel } from '@modules/media/models/media-document.model';
import { MediaImageInterface, MediaImageModel } from '@modules/media/models/media-image.model';
import { MediaTechnicalDataInterface, MediaTechnicalDataModel } from '@modules/media/models/media-technical-data-model';
import { MediaVideoInterface, MediaVideoModel } from '@modules/media/models/media-video.model';
import { ToastService } from '@modules/toast/toast.service';
import { Locale } from 'src/environments/language.config';

import { BufferingComponent } from '../../ui/buffering/buffering.component';
import { LightboxContextLink } from '../interfaces/lightbox.interface';
import { LightboxService, LightboxState } from '../services/lightbox.service';

const ALIASES: any = {
  structured_image: 'images',
  structured_images: 'images',
  image: 'images',
  video: 'videos',
  audio: 'audios',
  document: 'documents',
  technical_data: 'technical_data',
};

const TYPE_PARAMS: any = {
  images: {
    include: [
      // have to check when resourceType is images
      'alternate_images',
      'alternate_images.image',
      'alternate_images.adjusted_image',
      'image',
      'adjusted_image',
    ].join(),
  },
  documents: { include: 'file' },
  technical_data: { include: 'file' },
  videos: { include: 'video' },
  audios: { include: 'audio' },
};
@Component({
  template: `@if ((isLoading$ | async) || false) {
    <div class="loader-overlay">
      <mb-buffering></mb-buffering>
    </div>
  }`,
  styles: [
    `
      .loader-overlay {
        position: fixed;
        width: 100%;
        height: 100%;
        z-index: 800;
        top: 0;
        left: 0;
        background-color: rgba(0, 0, 0, 0.8);
        backdrop-filter: blur(10px);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BufferingComponent, AsyncPipe],
})
export class LightboxPageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private apiService = inject(ContentAPIService);
  private lightboxService = inject(LightboxService);
  private inputSanitizer = inject(InputSanitizer);
  private mediaAdapter = inject(MediaAdapter);
  private translateService = inject(TranslateService);
  private toastService = inject(ToastService);

  private destroy$ = new Subject<boolean>();
  private _isLoading$ = new BehaviorSubject<boolean>(true);
  public isLoading$ = this._isLoading$.asObservable();

  ngOnInit() {
    // subscribe to changes of the params and push them to the lightbox
    let resourceId = this.inputSanitizer.slug(this.route.snapshot.paramMap.get('resourceId') as string) as string;
    let resourceType = this.inputSanitizer.resourceType(
      this.route.snapshot.paramMap.get('resourceType') as string
    ) as string;

    resourceType = ALIASES[resourceType] || resourceType;
    const params = new HttpParams().appendAll(TYPE_PARAMS[resourceType] || {});
    let context: FavoriteDataContextInterface | undefined = undefined;
    let contextLink: LightboxContextLink;
    let slug = '';
    const currentLang = this.translateService.currentLang()?.split('-')[0];
    const snapshotData = this.route.parent?.snapshot.data || {};

    const isMagazine = !!snapshotData.magazine;
    const parentData = snapshotData.magazine || snapshotData.article;

    if (parentData) {
      context = {
        id: parentData.id,
        type: isMagazine ? 'page' : 'article',
      };

      if (isMagazine) {
        const slug = parentData.slugs.find(({ lang }: { lang: string }) => lang === currentLang)?.value;
        contextLink = {
          label: 'CONNECTED_PAGE',
          text: parentData.title,
          link: {
            url: slug,
          },
        };
      } else {
        contextLink = {
          label: 'CONNECTED_ARTICLE',
          text: parentData.title,
          link: {
            url: `/article/${parentData.id}`,
          },
        };
      }
    } else if (snapshotData.topic) {
      // probably on a content hub page
      // FIXME: clean up page handling / router data etc.)
      if (snapshotData.topic.show_topic_page) {
        context = {
          id: '',
          type: 'topic_page',
        };
        slug = snapshotData.topic.path[this.translateService.currentLang() ?? Locale.DE] || '';
      } else {
        context = {
          id: '',
          type: 'content_hub',
        };
        slug = snapshotData.topic.path[this.translateService.currentLang() ?? Locale.DE] || '';
      }
    }

    this.apiService
      .getResource(resourceType, resourceId, params)
      .pipe(
        takeUntil(this.destroy$),
        map((data) => {
          switch (data.type) {
            case 'image':
            case 'structured_image':
              return new MediaImageModel(
                this.mediaAdapter.parse(data, undefined, context, slug) as MediaImageInterface
              );
            case 'document':
              return new MediaDocumentModel(
                this.mediaAdapter.parse(data, undefined, context, slug) as MediaDocumentInterface
              );
            case 'technical_data':
              return new MediaTechnicalDataModel(
                this.mediaAdapter.parse(data, undefined, context, slug) as MediaTechnicalDataInterface
              );
            case 'video':
              return new MediaVideoModel(
                this.mediaAdapter.parse(data, undefined, context, slug) as MediaVideoInterface
              );
            case 'audio':
              return new MediaAudioModel(
                this.mediaAdapter.parse(data, undefined, context, slug) as MediaAudioInterface
              );
          }
          throw Error('Unknown resource type');
        }),
        catchError(() => {
          // TODO: improve error handling when re-disignend for workbench 2.0
          setTimeout(() => {
            this.toastService.open({ text: 'AN_ERROR_OCCURED_MESSAGE' });
            this.router.navigate(['../'], { relativeTo: this.route });
          }, 400);
          return EMPTY;
        })
      )
      .subscribe((resource) => {
        this._isLoading$.next(false);
        if (resource) {
          this.lightboxService.openMedia(resource, contextLink);
          this.lightboxService.lightboxState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
            if (state === LightboxState.CLOSED) {
              // leave lightbox subroute
              this.router.navigate(['../..'], { relativeTo: this.route, queryParamsHandling: 'merge' });
            }
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.lightboxService.close();
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
